import { ReactNode } from 'react';
import { withDot } from './wrappers';
import { color } from '@nuvolo/nuux-theme/utils';

interface PriorityItem {
  key: string | number;
  label: string;
  value?: number;
}

interface RenderPriorityDotOptions<T> {
  item: T;
  priorityItems: PriorityItem[] | Record<string | number, PriorityItem>;
  priorityColorMap: Record<string | number, string>;
  getPriorityValue: (item: T) => number | string | null | undefined;
}

/**
 * Generic helper function to render priority dot for list items
 * @param options Configuration object containing item, priority items, color map, and value getter
 * @returns ReactNode with priority dot or null
 */
export function renderPriorityDot<T>(options: RenderPriorityDotOptions<T>): ReactNode {
  const { item, priorityItems, priorityColorMap, getPriorityValue } = options;

  const priorityValue = getPriorityValue(item);
  if (!priorityValue) return null;

  let priority: PriorityItem | undefined;

  // Handle array of priority items
  if (Array.isArray(priorityItems)) {
    priority = priorityItems.find((p) => p.key === priorityValue);
  }
  // Handle object/record of priority items
  else {
    priority = Object.values(priorityItems).find((p) => p.value === priorityValue);
  }

  if (!priority) return null;

  const priorityColor = priorityColorMap[priority.key] || color('ui', 'dark');
  return withDot(priority.label, priorityColor);
}
